import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import usePermission from "../../utils/usePermission";
import ReportApiViewController from "../../redux/actions/ReportApiAction";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ReportsMobileView from "./ReportsMobileView";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";

const MONTH_OPTIONS = [
  { value: 1, name: "January" },
  { value: 2, name: "February" },
  { value: 3, name: "March" },
  { value: 4, name: "April" },
  { value: 5, name: "May" },
  { value: 6, name: "June" },
  { value: 7, name: "July" },
  { value: 8, name: "August" },
  { value: 9, name: "September" },
  { value: 10, name: "October" },
  { value: 11, name: "November" },
  { value: 12, name: "December" },
];

const ReportListPage = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hasPermission } = usePermission();

  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const { getEmployeeForReport, generatePFECRReport, genrateESICReport } =
    ReportApiViewController();
  const { report_data } = useSelector((state) => state.report);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const canExport = hasPermission("report_management", "export");

  useEffect(() => {
    dispatch(getEmployeeForReport());
  }, [dispatch]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear; year >= 2020; year -= 1) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const monthKey = useMemo(
    () => `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`,
    [selectedYear, selectedMonth]
  );

  const periodLabel = useMemo(() => {
    const monthName =
      MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.name || selectedMonth;
    return `${monthName} ${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const employees = report_data || [];

  const displayedEmployees = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    const q = searchTerm.toLowerCase();
    return employees.filter(
      (emp) =>
        emp.first_name?.toLowerCase().includes(q) ||
        emp.last_name?.toLowerCase().includes(q) ||
        emp.employee_id?.toLowerCase().includes(q)
    );
  }, [employees, searchTerm]);

  const clearEmployeeSelection = () => {
    gridRef.current?.deselectAll();
    setSelectedRows([]);
    setShowSuccessDialog(false);
  };

  const handleToggleSelect = useCallback((employee) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === employee.id);
      if (exists) return prev.filter((row) => row.id !== employee.id);
      return [...prev, employee];
    });
  }, []);

  const handleDownloadPF = useCallback(
    (employee) => {
      dispatch(
        generatePFECRReport({
          employee_id: employee.id,
          month: monthKey,
        })
      );
    },
    [dispatch, generatePFECRReport, monthKey]
  );

  const handleDownloadESIC = useCallback(
    (employee) => {
      dispatch(
        genrateESICReport({
          employee_id: [employee.id],
          month: monthKey,
        })
      );
    },
    [dispatch, genrateESICReport, monthKey]
  );

  const handleBulkPF = useCallback(async () => {
    try {
      await dispatch(
        generatePFECRReport({
          employee_id: selectedRows.map((row) => row.id),
          month: monthKey,
        })
      );
      setShowSuccessDialog(true);
    } catch {
      // toast handled in action
    }
  }, [dispatch, generatePFECRReport, selectedRows, monthKey]);

  const handleBulkESIC = useCallback(async () => {
    try {
      await dispatch(
        genrateESICReport({
          employee_id: selectedRows.map((row) => row.id),
          month: monthKey,
        })
      );
      setShowSuccessDialog(true);
    } catch {
      // toast handled in action
    }
  }, [dispatch, genrateESICReport, selectedRows, monthKey]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        maxWidth: 50,
        pinned: "left",
        colId: "checkboxColumn",
      },
      {
        headerName: "Emp ID",
        field: "employee_id",
        flex: 1,
        maxWidth: 100,
        pinned: "left",
      },
      {
        headerName: "Name",
        valueGetter: (params) =>
          `${params.data.first_name || ""} ${params.data.last_name || ""}`.trim(),
        flex: 1,
        minWidth: 140,
      },
      {
        headerName: "PF_ECR",
        field: "pf_ecr",
        width: 90,
        cellRenderer: (params) => (
          <Tooltip title="Download PF ECR">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleDownloadPF(params.data)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        headerName: "ESIC",
        field: "esic",
        width: 90,
        cellRenderer: (params) => (
          <Tooltip title="Download ESIC">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleDownloadESIC(params.data)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleDownloadPF, handleDownloadESIC]
  );

  if (isMobile) {
    return (
      <>
        <ReportsMobileView
          employees={employees}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          monthOptions={MONTH_OPTIONS}
          yearOptions={yearOptions}
          periodLabel={periodLabel}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onBulkPF={handleBulkPF}
          onBulkESIC={handleBulkESIC}
          onDownloadPF={handleDownloadPF}
          onDownloadESIC={handleDownloadESIC}
          canExport={canExport}
        />
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          onClear={clearEmployeeSelection}
          count={selectedRows.length}
          isMobile={isMobile}
        />
      </>
    );
  }

  return (
    <div>
      <div
        className="d-flex flex-wrap mb-3"
        style={{ gap: "12px", alignItems: "center" }}
      >
        {canExport && (
          <>
            <Button
              variant="contained"
              disabled={selectedRows.length === 0}
              color="primary"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                height: "40px",
                fontSize: "0.875rem",
              }}
              onClick={handleBulkPF}
            >
              <DownloadIcon fontSize="small" className="me-1" />
              PF_ECR
            </Button>
            <Button
              variant="contained"
              disabled={selectedRows.length === 0}
              color="primary"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                height: "40px",
                fontSize: "0.875rem",
              }}
              onClick={handleBulkESIC}
            >
              <DownloadIcon fontSize="small" className="me-1" />
              ESIC
            </Button>
          </>
        )}

        <TextField
          size="small"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <PageSizeSelector
          pageSize={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        />
      </div>

      <div className="label-card">
        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
          <h3 className="mb-0">Reports List</h3>
          <div className="d-flex gap-3">
            <FormControl size="small" style={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              >
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" style={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div className="list-grid-container" style={{ height: "70vh" }}>
        <ReusableGrid
          rowData={displayedEmployees}
          columnDefs={columnDefs}
          gridRef={gridRef}
          pagination={true}
          pageSize={pageSize}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
          emptyMessage="No employees found"
          emptyHint="Adjust your search or try refreshing the page."
        />
      </div>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        onClear={clearEmployeeSelection}
        count={selectedRows.length}
        isMobile={isMobile}
      />
    </div>
  );
};

const SuccessDialog = ({ open, onClose, onClear, count, isMobile }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="xs"
    PaperProps={{ sx: { mx: isMobile ? 2 : undefined, borderRadius: "12px" } }}
  >
    <DialogTitle>
      <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: BRAND.navy }}>
        Report Generated
      </Typography>
    </DialogTitle>
    <DialogContent>
      <DialogContentText>
        Report downloaded for <strong>{count}</strong> employee
        {count > 1 ? "s" : ""}. Clear the current selection?
      </DialogContentText>
    </DialogContent>
    <DialogActions
      sx={{
        px: isMobile ? 2 : 3,
        pb: 2,
        flexDirection: isMobile ? "column" : "row",
        gap: 1,
      }}
    >
      <Button
        onClick={onClear}
        variant="contained"
        fullWidth={isMobile}
        sx={{ ...primaryButtonSx, ...(isMobile && { minHeight: 48 }) }}
      >
        Clear Selection
      </Button>
      <Button
        onClick={onClose}
        variant="outlined"
        color="inherit"
        fullWidth={isMobile}
        sx={isMobile ? { minHeight: 44 } : undefined}
      >
        Keep Selection
      </Button>
    </DialogActions>
  </Dialog>
);

export default ReportListPage;
